import React, { useEffect, useMemo, useState } from 'react';
import { Table, TableProps } from 'antd';
import type { ColumnGroupType, ColumnType } from 'antd/es/table';
import { Resizable, ResizableProps } from 'react-resizable';
import styled from 'styled-components';
import 'react-resizable/css/styles.css';

interface ResizableColumnConfig<RecordType> {
  minWidth?: number;
  maxWidth?: number;
  width?: number | string;
  fixed?: 'left' | 'right' | boolean;
  children?: ResizableColumnType<RecordType>[];
}

type ResizableColumnType<RecordType> =
  | (ColumnType<RecordType> & ResizableColumnConfig<RecordType>)
  | (ColumnGroupType<RecordType> & ResizableColumnConfig<RecordType>);

type ResizableColumnsType<RecordType> = ResizableColumnType<RecordType>[];

type ResizableTableProps<RecordType> = Omit<TableProps<RecordType>, 'columns'> & {
  columns?: ResizableColumnsType<RecordType>;
};

const DEFAULT_WIDTH = 150;
const DEFAULT_MIN_WIDTH = 50;

// 将列宽拖拽限制在右侧边界，避免默认的 20px 旋转手柄遮挡筛选按钮。
const ColumnResizeHandle = styled.span`
  && {
    top: 0;
    right: 0;
    width: 4px;
    height: 100%;
    margin-top: 0;
    padding: 0;
    transform: none;
    background-image: none;
    cursor: col-resize;
  }
`;

const isLeafColumn = <RecordType extends object>(
  column: ResizableColumnType<RecordType>
): column is ColumnType<RecordType> & ResizableColumnConfig<RecordType> => {
  return !column.children || column.children.length === 0;
};

const getColumnIdentity = <RecordType extends object>(
  column: ResizableColumnType<RecordType>,
  path: number[],
) => {
  if (column.key != null) {
    return `key:${String(column.key)}`;
  }

  if ('dataIndex' in column && column.dataIndex != null) {
    const dataIndex = Array.isArray(column.dataIndex)
      ? column.dataIndex.join('.')
      : String(column.dataIndex);

    return `dataIndex:${dataIndex}`;
  }

  return `path:${path.join('.')}`;
};

const collectColumnWidths = <RecordType extends object>(
  columns?: ResizableColumnsType<RecordType>,
  parentPath: number[] = [],
  widthMap: Record<string, number> = {},
) => {
  columns?.forEach((column, index) => {
    const path = [...parentPath, index];

    if (!isLeafColumn(column)) {
      // 分组表头本身不直接拖拽，只记录叶子列的宽度。
      collectColumnWidths(column.children, path, widthMap);
      return;
    }

    widthMap[getColumnIdentity(column, path)] =
      typeof column.width === 'number' ? column.width : DEFAULT_WIDTH;
  });

  return widthMap;
};

const getColumnAtPath = <RecordType extends object>(
  columns: ResizableColumnsType<RecordType> | undefined,
  path: number[],
) => {
  let currentColumns = columns;
  let currentColumn: ResizableColumnType<RecordType> | undefined;

  for (const index of path) {
    currentColumn = currentColumns?.[index];

    if (!currentColumn) {
      return undefined;
    }

    currentColumns = !isLeafColumn(currentColumn) ? currentColumn.children : undefined;
  }

  return currentColumn;
};

const ResizableTitle = (
  props: React.ThHTMLAttributes<HTMLTableHeaderCellElement> & {
    onResize?: (e: React.SyntheticEvent, data: { size: { width: number } }) => void;
    width: number;
    minWidth?: number;
    maxWidth?: number;
    fixed?: 'left' | 'right' | boolean;
  },
) => {
  const { width, minWidth = DEFAULT_MIN_WIDTH, maxWidth = Infinity, onResize, fixed, ...restProps } = props;

  if (!width || !onResize) {
    return <th {...restProps} />;
  }

  const resizableProps: ResizableProps = {
    width,
    height: 0,
    handle: (
      <ColumnResizeHandle
        data-testid="resizable-column-handle-e"
        className="react-resizable-handle react-resizable-handle-e"
      />
    ),
    resizeHandles: ['e'],
    minConstraints: [minWidth, 0],
    maxConstraints: [maxWidth, 0],
    onResize,
    draggableOpts: { enableUserSelectHack: true },
  };

  return (
    <Resizable {...resizableProps}>
      <th {...restProps} style={{ ...restProps.style, position: fixed ? 'sticky' : undefined }} />
    </Resizable>
  );
};

const ResizableTable = <RecordType extends object = any>(
  props: ResizableTableProps<RecordType>,
) => {
  const { columns, components, ...tableProps } = props;
  const [columnWidthMap, setColumnWidthMap] = useState<Record<string, number>>(() =>
    collectColumnWidths(columns),
  );

  useEffect(() => {
    // 外部 columns 更新时，先按最新列配置重建默认宽度，
    // 再把当前页面生命周期里用户拖拽过的宽度覆盖回去。
    setColumnWidthMap((currentWidthMap) => ({
      ...collectColumnWidths(columns),
      ...currentWidthMap,
    }));
  }, [columns]);

  const mergedColumns = useMemo(() => {
    const attachResizeProps = (
      nextColumns?: ResizableColumnsType<RecordType>,
      parentPath: number[] = [],
    ): ResizableColumnsType<RecordType> | undefined => {
      return nextColumns?.map((column, index): ResizableColumnType<RecordType> => {
        const path = [...parentPath, index];

        if (!isLeafColumn(column)) {
          return {
            ...column,
            children: attachResizeProps(column.children, path),
          };
        }

        const identity = getColumnIdentity(column, path);
        const width = columnWidthMap[identity] ?? DEFAULT_WIDTH;

        return {
          ...column,
          width,
          minWidth: column.minWidth ?? DEFAULT_MIN_WIDTH,
          maxWidth: column.maxWidth ?? Infinity,
          onHeaderCell: (currentColumn) => {
            const originHeaderCell = column.onHeaderCell?.(currentColumn as never);

            return {
              ...originHeaderCell,
              width,
              minWidth: column.minWidth ?? DEFAULT_MIN_WIDTH,
              maxWidth: column.maxWidth ?? Infinity,
              fixed: column.fixed,
              onResize: (
                _: React.SyntheticEvent,
                { size }: { size: { width: number } },
              ) => {
                const resizedColumn = getColumnAtPath(columns, path);

                if (!resizedColumn) {
                  return;
                }

                const resizedColumnIdentity = getColumnIdentity(resizedColumn, path);

                // 按列标识保存最新宽度，这样表头会立刻重渲染，
                // 即使外部下一次 render 重新创建了 columns 数组也能对上。
                setColumnWidthMap((currentWidthMap) => ({
                  ...currentWidthMap,
                  [resizedColumnIdentity]: size.width,
                }));
              },
            } as React.ThHTMLAttributes<HTMLTableHeaderCellElement> & {
              width: number;
              minWidth: number;
              maxWidth: number;
              fixed?: 'left' | 'right' | boolean;
              onResize?: (e: React.SyntheticEvent, data: { size: { width: number } }) => void;
            };
          },
        };
      });
    };

    return attachResizeProps(columns);
  }, [columnWidthMap, columns]);

  const mergedComponents: TableProps<RecordType>['components'] = {
    ...components,
    header: {
      // 保留调用方自定义的 Table components，
      // 只替换表头单元格，让 antd 能把拖拽参数传给每个叶子列。
      ...components?.header,
      cell: ResizableTitle,
    },
  };

  return (
    <Table
      {...tableProps}
      columns={mergedColumns}
      components={mergedComponents}
    />
  );
};

export default ResizableTable;
